import dayjs, { Dayjs } from "dayjs";
import { EMPTY_STR } from "@/constants/string";

// Filter shape for the ExamSteps detail page. Multi-select fields use plural names.
// Date ranges are kept as Dayjs/null to make controlled date inputs work cleanly.
export type TExamStepsFilterFormExtended = {
  fullName: string;
  rsoCompanyIds: string[];
  nationalityIds: string[];
  procedureStatuses: string[];
  nextProcedureEndDateFrom: Dayjs | null;
  nextProcedureEndDateTo: Dayjs | null;
  examMonthFrom: Dayjs | null;
  examMonthTo: Dayjs | null;
  registeredAtFrom: Dayjs | null;
  registeredAtTo: Dayjs | null;
};

export const defaultExamStepsFilterValues: TExamStepsFilterFormExtended = {
  fullName: "",
  rsoCompanyIds: [],
  nationalityIds: [],
  procedureStatuses: [],
  nextProcedureEndDateFrom: null,
  nextProcedureEndDateTo: null,
  examMonthFrom: null,
  examMonthTo: null,
  registeredAtFrom: null,
  registeredAtTo: null,
};

const EXAM_STEPS_DATE_KEYS: (keyof TExamStepsFilterFormExtended)[] = [
  "nextProcedureEndDateFrom",
  "nextProcedureEndDateTo",
  "examMonthFrom",
  "examMonthTo",
  "registeredAtFrom",
  "registeredAtTo",
];

const EXAM_STEPS_MULTI_SELECT_KEYS: (keyof TExamStepsFilterFormExtended)[] = [
  "rsoCompanyIds",
  "nationalityIds",
  "procedureStatuses",
];

// Re-hydrate Dayjs after localStorage JSON round-trip.
export const normalizeExamStepsDateFields = (
  filters: Partial<TExamStepsFilterFormExtended>,
): Partial<TExamStepsFilterFormExtended> => {
  const result: Partial<Record<keyof TExamStepsFilterFormExtended, unknown>> = { ...filters };
  for (const key of EXAM_STEPS_DATE_KEYS) {
    const value = result[key];
    if (value && typeof value === "string") {
      const day = dayjs(value);
      result[key] = day.isValid() ? day : null;
    }
  }
  return result as Partial<TExamStepsFilterFormExtended>;
};

// Coerce scalar values on multi-select keys into single-element arrays (e.g. legacy saved entries).
export const normalizeExamStepsLegacyFields = (
  filters: Partial<Record<string, unknown>>,
): Partial<TExamStepsFilterFormExtended> => {
  const result: Partial<Record<string, unknown>> = { ...filters };
  for (const key of EXAM_STEPS_MULTI_SELECT_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TExamStepsFilterFormExtended>;
};

export const isEmptyExamStepsFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

// Per-field serialization format for date keys. Fields not listed fall back to ISO string.
// `examMonthFrom`/`examMonthTo` use YYYY/MM because BE expects month-granularity strings.
const EXAM_STEPS_DATE_SERIALIZATION_FORMATS: Partial<Record<keyof TExamStepsFilterFormExtended, string>> = {
  examMonthFrom: "YYYY/MM",
  examMonthTo: "YYYY/MM",
};

// Convert in-memory filter values to a localStorage- and API-safe payload.
export const stripEmptyExamStepsFilterFields = (filters: TExamStepsFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyExamStepsFilterValue(value)) continue;
    if (dayjs.isDayjs(value)) {
      const fmt = EXAM_STEPS_DATE_SERIALIZATION_FORMATS[key as keyof TExamStepsFilterFormExtended];
      result[key] = fmt ? value.format(fmt) : value.toISOString();
      continue;
    }
    result[key] = value;
  }
  return result;
};

// Field-by-field labels used by the save-condition modal preview (free-text + multi-select).
// Date ranges are listed separately via EXAM_STEPS_DATE_RANGE_FIELDS so they can be rendered as `from 〜 to`.
export const EXAM_STEPS_FILTER_FIELD_LABELS: { key: keyof TExamStepsFilterFormExtended; label: string }[] = [
  { key: "fullName", label: "氏名" },
  { key: "rsoCompanyIds", label: "所属機関" },
  { key: "nationalityIds", label: "国籍" },
  { key: "procedureStatuses", label: "手続きステータス" },
];

export const EXAM_STEPS_DATE_RANGE_FIELDS = {
  nextProcedureEndDate: {
    fromKey: "nextProcedureEndDateFrom" as const,
    toKey: "nextProcedureEndDateTo" as const,
    label: "次の手続き期日・期限",
  },
  examMonth: {
    fromKey: "examMonthFrom" as const,
    toKey: "examMonthTo" as const,
    label: "試験年月",
  },
  registeredAt: {
    fromKey: "registeredAtFrom" as const,
    toKey: "registeredAtTo" as const,
    label: "登録日",
  },
};

export const formatExamStepsDateValue = (value: Dayjs | string | null, format: string = "YYYY/MM/DD"): string => {
  if (!value) return EMPTY_STR.DATE;
  const day = dayjs(value);
  return day.isValid() ? day.format(format) : EMPTY_STR.DATE;
};
